import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowLeft, Download, Mail } from "lucide-react";
import OwnerNotifyModal from "@/components/OwnerNotifyModal";

// Swiss date/CHF formatting
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T00:00` : iso);
  if (isNaN(d.getTime())) return iso;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T00:00` : iso);
  if (isNaN(d.getTime())) return iso;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};
const fmtChf = (n) => {
  const v = Number(n || 0);
  return Math.abs(v - Math.round(v)) < 0.005
    ? Math.round(v).toLocaleString("de-CH").replace(/,/g, "'") + ".–"
    : v.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, "'");
};

const Row = ({ label, value }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-1 sm:gap-4 py-3 border-b border-[var(--rs-border)] last:border-b-0">
    <div className="rs-overline whitespace-normal leading-tight">{label}</div>
    <div className="text-sm break-words">{value}</div>
  </div>
);

const ContractDetail = () => {
  const { id } = useParams();
  const { t } = useLanguage();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  const [ownerOpen, setOwnerOpen] = useState(false);

  const reload = () => {
    api.get(`/contracts/${id}`).then(({ data }) => setC(data)).catch(() => setErr("Vertrag nicht gefunden"));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const download = async () => {
    const res = await api.get(`/contracts/${id}/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${c.contract_number}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  if (err) {
    return (
      <div className="rs-app min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <div className="rs-overline">404</div>
          <h1 className="font-[Chivo] text-3xl font-black mt-2">{err}</h1>
          <Link to="/admin" className="rs-btn-secondary mt-6 inline-flex">
            <ArrowLeft size={14} className="mr-2" /> Zurück
          </Link>
        </div>
      </div>
    );
  }

  if (!c) {
    return (
      <div className="rs-app min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-16 text-center text-[var(--rs-text-muted)]">…</div>
      </div>
    );
  }

  return (
    <div className="rs-app min-h-screen bg-white">
      <Header />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        <Link to="/admin" className="text-xs uppercase tracking-[0.2em] text-[var(--rs-text-muted)] hover:text-[var(--rs-text)] inline-flex items-center gap-2" data-testid="detail-back">
          <ArrowLeft size={12} /> {t("admin_title")}
        </Link>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mt-3">
          <div>
            <div className="rs-overline">{t("contract_number")}</div>
            <h1 className="font-[Chivo] text-4xl font-black tracking-tight" data-testid="detail-number">
              {c.contract_number}
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={`rs-btn-secondary ${c.owner_payout ? "!text-[var(--rs-primary)]" : ""}`}
              onClick={() => setOwnerOpen(true)}
              title={c.owner_payout ? `Bereits benachrichtigt am ${new Date(c.owner_payout.notified_at).toLocaleDateString("de-CH")}` : "Besitzer melden"}
              data-testid="detail-notify-owner-btn"
            >
              <Mail size={16} className="mr-2" />
              {c.owner_payout ? "Besitzer erneut melden" : "Besitzer melden"}
            </button>
            <button type="button" className="rs-btn-primary" onClick={download} data-testid="detail-pdf-btn">
              <Download size={16} className="mr-2" />
              {t("download_pdf")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
          <section>
            <h2 className="rs-overline mb-3">{t("th_vehicle")}</h2>
            <div className="border-2 border-[var(--rs-text)] p-5">
              <Row label={t("th_vehicle")} value={`${c.vehicle_name} (${c.vehicle_plate})`} />
              <Row label={t("th_period")} value={`${fmtDateTime(c.rental_start)} → ${fmtDateTime(c.rental_end)}`} />
              <Row label={t("summary_price")} value={`CHF ${fmtChf(c.price_chf)}`} />
              <Row label={t("summary_deposit")} value={`CHF ${fmtChf(c.deposit_chf)}`} />
              <Row label={t("summary_deductible")} value={`CHF ${fmtChf(c.deductible_chf)}`} />
            </div>
          </section>
          <section>
            <h2 className="rs-overline mb-3">{t("th_customer")}</h2>
            <div className="border-2 border-[var(--rs-text)] p-5">
              <Row label={t("f_full_name")} value={c.customer.full_name} />
              {c.customer.company && <Row label="Firma" value={c.customer.company} />}
              <Row
                label={t("f_address")}
                value={`${c.customer.address}, ${c.customer.postal_code} ${c.customer.city}`}
              />
              <Row label={t("f_phone")} value={c.customer.phone} />
              <Row label={t("f_email")} value={c.customer.email || "—"} />
              <Row label="Führerschein-Nr." value={c.customer.license_number} />
              {c.customer.date_of_birth && <Row label={t("f_dob")} value={fmtDate(c.customer.date_of_birth)} />}
            </div>
          </section>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
          <section>
            <h2 className="rs-overline mb-3">{t("step_id")}</h2>
            <div className="grid grid-cols-1 gap-3">
              <div className="border-2 border-[var(--rs-text)] bg-[var(--rs-surface)] aspect-[1.586/1] overflow-hidden">
                {c.id_photo_base64 ? (
                  <img src={c.id_photo_base64} alt="ID Vorderseite" className="w-full h-full object-cover" data-testid="detail-id-photo" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[var(--rs-text-muted)]">—</div>
                )}
              </div>
              {c.id_photo_back_base64 && (
                <div className="border-2 border-[var(--rs-border)] bg-[var(--rs-surface)] aspect-[1.586/1] overflow-hidden">
                  <img src={c.id_photo_back_base64} alt="ID Rückseite" className="w-full h-full object-cover" data-testid="detail-id-back" />
                </div>
              )}
            </div>
          </section>
          <section>
            <h2 className="rs-overline mb-3">{t("step_signature")}</h2>
            <div className="border-2 border-[var(--rs-text)] bg-white h-64 flex items-center justify-center overflow-hidden">
              {c.signature_base64 ? (
                <img src={c.signature_base64} alt="Signature" className="max-w-full max-h-full object-contain" data-testid="detail-signature" />
              ) : (
                <div className="text-[var(--rs-text-muted)]">—</div>
              )}
            </div>
          </section>
        </div>

        {(c.handover_photo_base64 || c.return_photo_base64) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            <section>
              <h2 className="rs-overline mb-3">
                {t("handover_photo_label")}{c.odometer_start != null ? ` · ${c.odometer_start} km` : ""}
              </h2>
              <div className="border-2 border-[var(--rs-text)] bg-[var(--rs-surface)] aspect-[4/3] overflow-hidden">
                {c.handover_photo_base64 ? (
                  <img src={c.handover_photo_base64} alt="Handover" className="w-full h-full object-cover" data-testid="detail-handover-photo" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[var(--rs-text-muted)]">—</div>
                )}
              </div>
            </section>
            <section>
              <h2 className="rs-overline mb-3">
                {t("return_photo_label")}{c.odometer_end != null ? ` · ${c.odometer_end} km` : ""}
              </h2>
              <div className="border-2 border-[var(--rs-text)] bg-[var(--rs-surface)] aspect-[4/3] overflow-hidden">
                {c.return_photo_base64 ? (
                  <img src={c.return_photo_base64} alt="Return" className="w-full h-full object-cover" data-testid="detail-return-photo" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[var(--rs-text-muted)]">—</div>
                )}
              </div>
            </section>
          </div>
        )}

        {(c.agb_accepted || c.defects_note) && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            {c.agb_accepted && (
              <section className="bg-[var(--rs-primary)]/5 border-l-4 border-[var(--rs-primary)] p-5" data-testid="detail-agb">
                <h2 className="rs-overline mb-2">{t("agb_title")}</h2>
                <div className="font-bold text-[var(--rs-primary)]">✓ Akzeptiert</div>
                <div className="text-xs text-[var(--rs-text-muted)] mt-1">
                  Version {c.agb_version || "—"}
                  {c.agb_accepted_at && <> · {(c.agb_accepted_at || "").slice(0, 19).replace("T", " ")}</>}
                </div>
              </section>
            )}
            {c.defects_note && (
              <section className="bg-white border-2 border-[var(--rs-warning)] p-5" data-testid="detail-defects">
                <h2 className="rs-overline mb-2">{t("defects_label")}</h2>
                <div className="text-sm whitespace-pre-wrap">{c.defects_note}</div>
              </section>
            )}
          </div>
        )}
        {c.owner_payout && (
          <div className="mt-8 bg-[var(--rs-primary)]/5 border-l-4 border-[var(--rs-primary)] p-5" data-testid="detail-owner-payout">
            <h2 className="rs-overline mb-2">Besitzer-Benachrichtigung</h2>
            <div className="text-sm">
              An <strong>{c.owner_payout.owner_name || c.owner_payout.owner_email}</strong> ·
              {" "}{new Date(c.owner_payout.notified_at).toLocaleString("de-CH")}
            </div>
            <div className="text-sm mt-1">
              Provision NIVARA ({c.owner_payout.commission_pct}%) − CHF {fmtChf(c.owner_payout.commission_chf)}
            </div>
            <div className="text-sm font-bold text-[var(--rs-primary)] mt-1">
              Auszahlung: CHF {fmtChf(c.owner_payout.payout_chf)}
            </div>
          </div>
        )}
      </div>

      <OwnerNotifyModal
        open={ownerOpen}
        contract={c}
        onClose={() => setOwnerOpen(false)}
        onSent={reload}
      />
    </div>
  );
};

export default ContractDetail;
